/**
 * Common OCR validation utilities
 */

// Age validation function
export const getAge = (inputDate) => {
    if (!inputDate) return null;
    const today = new Date();
    const birthDate = new Date(inputDate);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
};

// Calculate ages and flags for multiple dates
export const calculateAgesAndFlags = (dates, minAge = 18, maxAge = 65) => {
    if (!dates || dates.length === 0) return [];
    return dates.map(date => {
        const age = getAge(date);
        const ageFlag = age !== null && (age < minAge || age > maxAge);
        return { age, ageFlag };
    });
};

// Phone number validation
export const validatePhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return { isValid: false, error: 'Phone number is required' };
    
    const cleanedNumber = phoneNumber.replace(/\D/g, '');
    if (cleanedNumber.length !== 10) {
        return { isValid: false, error: 'Please enter a valid 10-digit phone number' };
    }
    
    return { isValid: true, error: null };
};

// Name validation
export const validateName = (name, fieldName = 'Name') => {
    if (!name || !name.trim()) {
        return { isValid: false, error: `${fieldName} is required` };
    }
    
    if (name.trim().length < 2) {
        return { isValid: false, error: `${fieldName} must be at least 2 characters long` };
    }
    
    return { isValid: true, error: null };
};

// Date validation
export const validateDate = (date, fieldName = 'Date') => {
    if (!date) {
        return { isValid: false, error: `${fieldName} is required` };
    }
    
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) {
        return { isValid: false, error: `Please enter a valid ${fieldName.toLowerCase()}` };
    }
    
    return { isValid: true, error: null };
};

// Age validation for OCR flow
export const validateAge = (dob, minAge = 18, maxAge = 65, context = 'beneficiary') => {
    if (!dob) {
        return { isValid: false, error: 'Date of birth is required' };
    }
    
    const age = getAge(dob);
    if (age === null) {
        return { isValid: false, error: 'Invalid date of birth' };
    }
    
    if (age < minAge) {
        return { 
            isValid: false, 
            error: context === 'beneficiary' 
                ? `${context.charAt(0).toUpperCase() + context.slice(1)} must be at least ${minAge} years old`
                : `Age should be ${minAge}+`
        };
    }
    
    if (age > maxAge) {
        return { 
            isValid: false, 
            error: context === 'beneficiary'
                ? `${context.charAt(0).toUpperCase() + context.slice(1)} must be under ${maxAge} years old`
                : `Age must be under ${maxAge} years`
        };
    }
    
    return { isValid: true, error: null, age };
};

// Gender validation
export const validateGender = (gender) => {
    const validGenders = ['MALE', 'FEMALE', 'male', 'female', 'M', 'F'];
    if (!gender) {
        return { isValid: false, error: 'Gender is required' };
    }
    
    if (!validGenders.includes(gender)) {
        return { isValid: false, error: 'Please select a valid gender' };
    }
    
    return { isValid: true, error: null };
};

// Relation validation for nominees
export const validateRelation = (relation, relations = []) => {
    if (!relation) {
        return { isValid: false, error: 'Please select a relation' };
    }
    
    if (relations.length > 0 && !relations.some(r => r.value === relation || r === relation)) {
        return { isValid: false, error: 'Please select a valid relation' };
    }
    
    return { isValid: true, error: null };
};

// Comprehensive form validation
export const validateOCRForm = (formData, options = {}) => {
    const {
        requirePhone = true,
        requireEmail = false,
        requireRelation = false,
        relations = [],
        minAge = 18,
        maxAge = 65,
        context = 'beneficiary'
    } = options;
    
    const errors = {};
    
    // Name validation
    const nameValidation = validateName(formData.full_name, 'Full name');
    if (!nameValidation.isValid) {
        errors.full_name = nameValidation.error;
    }
    
    // Date of birth validation
    const dateValidation = validateDate(formData.dob, 'Date of birth');
    if (!dateValidation.isValid) {
        errors.dob = dateValidation.error;
    } else {
        // Age validation
        const ageValidation = validateAge(formData.dob, minAge, maxAge, context);
        if (!ageValidation.isValid) {
            errors.age = ageValidation.error;
        }
    }
    
    // Gender validation
    const genderValidation = validateGender(formData.gender);
    if (!genderValidation.isValid) {
        errors.gender = genderValidation.error;
    }
    
    // Phone validation
    if (requirePhone) {
        const phoneValidation = validatePhoneNumber(formData.phone_number);
        if (!phoneValidation.isValid) {
            errors.phone_number = phoneValidation.error;
        }
    }
    
    // Relation validation
    if (requireRelation) {
        const relationValidation = validateRelation(formData.relation, relations);
        if (!relationValidation.isValid) {
            errors.relation = relationValidation.error;
        }
    }
    
    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
};

// File validation utilities
export const validateFileType = (file, allowedTypes = ['.jpg', '.jpeg', '.png', '.pdf']) => {
    if (!file) return { isValid: false, error: 'No file selected' };
    
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    const isValidType = allowedTypes.includes(fileExtension) || 
                       allowedTypes.includes(file.type);
    
    if (!isValidType) {
        return { 
            isValid: false, 
            error: `Invalid file type. Allowed types: ${allowedTypes.join(', ')}` 
        };
    }
    
    return { isValid: true, error: null };
};

export const validateFileSize = (file, maxSize = 5 * 1024 * 1024) => {
    if (!file) return { isValid: false, error: 'No file selected' };
    
    if (file.size > maxSize) {
        const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(1);
        return { 
            isValid: false, 
            error: `File size exceeds ${maxSizeMB}MB limit` 
        };
    }
    
    return { isValid: true, error: null };
};

// Data transformation utilities
export const transformOCRData = (rawData, customMapping = {}) => {
    const defaultMapping = {
        full_name: rawData.name || rawData.full_name || '',
        dob: rawData.date_of_birth || rawData.dob || '',
        gender: rawData.gender || '',
        document_id: rawData.document_id || rawData.id_number || '',
        document_type: rawData.document_type || 'AADHAAR'
    };
    
    return { ...defaultMapping, ...customMapping };
};

// OCR response processors
export const processAdhaarOCR = (response) => {
    if (!response || !response.data) return {};
    
    return transformOCRData(response.data, {
        document_type: 'AADHAAR'
    });
};

export const processPANOCR = (response) => {
    if (!response || !response.data) return {};
    
    return transformOCRData(response.data, {
        document_type: 'PAN'
    });
};

export const processPassportOCR = (response) => {
    if (!response || !response.data) return {};
    
    return transformOCRData(response.data, {
        document_type: 'PASSPORT'
    });
};